/*
 * Copyright (c) 2008-present Sonatype, Inc.
 *
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/pro/attributions
 * Sonatype and Sonatype Nexus are trademarks of Sonatype, Inc. Apache Maven is a trademark of the Apache Foundation.
 * M2Eclipse is a trademark of the Eclipse Foundation. All other trademarks are the property of their respective owners.
 */
import React from 'react';
import { useMachine, useService } from '@xstate/react';
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { faAtlassian } from '@fortawesome/free-brands-svg-icons';
import CrowdSettingsMachine from './CrowdSettingsMachine';
import {
  ContentBody,
  FormUtils,
  NxButton,
  NxCheckbox,
  NxFontAwesomeIcon,
  NxForm,
  NxFormGroup,
  NxTextInput,
  NxTooltip,
  Page,
  PageHeader,
  PageTitle,
  Section
} from '@sonatype/nexus-ui-plugin';
import UIStrings from '../../../../constants/UIStrings';

export default function CrowdSettings() {
  const service = useMachine(CrowdSettingsMachine, {devTools: true})[2];
  return <CrowdSettingsForm service={service}/>;
}

export function CrowdSettingsForm({service}) {
  const [current, send] = useService(service);

  const {data, isPristine, loadError, saveError, validationErrors} = current.context;
  const isLoading = current.matches('loading');
  const isSaving = current.matches('saving');
  const isInvalid = FormUtils.isInvalid(validationErrors);

  function save() {
    send('SAVE');
  }

  function discard() {
    send('RESET');
  }

  function verifyConnection() {
    send('VERIFY-CONNECTION');
  }

  function clearCache() {
    send('CLEAR-CACHE');
  }

  function retry() {
    send('RETRY');
  }

  return <Page>
    <PageHeader><PageTitle icon={faAtlassian} {...UIStrings.CROWD_SETTINGS.MENU}/></PageHeader>
    <ContentBody className='nxrm-crowd-settings'>
      <Section>
        <NxForm
            loading={isLoading}
            loadError={loadError}
            doLoad={retry}
            onSubmit={save}
            submitError={saveError}
            submitMaskState={isSaving ? false : null}
            submitBtnText={UIStrings.SETTINGS.SAVE_BUTTON_LABEL}
            validationErrors={FormUtils.saveTooltip({isPristine, isInvalid})}
            additionalFooterBtns={<>
              <NxTooltip title={FormUtils.discardTooltip({isPristine})}>
                <NxButton type="button" className={(isPristine) && 'disabled'} onClick={discard}>
                  {UIStrings.SETTINGS.DISCARD_BUTTON_LABEL}
                </NxButton>
              </NxTooltip>
              <NxButton type="button" disabled={isInvalid} onClick={verifyConnection}>
                {UIStrings.CROWD_SETTINGS.BUTTONS.VERIFY_BUTTON_LABEL}
              </NxButton>
              <NxButton type="button" onClick={clearCache}>
                <NxFontAwesomeIcon icon={faTrash}/>
                <span>{UIStrings.CROWD_SETTINGS.BUTTONS.CLEAR_BUTTON_LABEL}</span>
              </NxButton>
            </>}>
          <NxFormGroup label={UIStrings.CROWD_SETTINGS.FIELDS.enabledLabel} isRequired>
            <NxCheckbox
                {...FormUtils.checkboxProps('enabled', current)}
                onChange={FormUtils.handleUpdate('enabled', send)}>
              {UIStrings.CROWD_SETTINGS.FIELDS.enabledDescription}
            </NxCheckbox>
          </NxFormGroup>
          <NxFormGroup label={UIStrings.CROWD_SETTINGS.FIELDS.realmActiveLabel} isRequired>
            <NxCheckbox
                {...FormUtils.checkboxProps('realmActive', current)}
                onChange={FormUtils.handleUpdate('realmActive', send)}>
              <span dangerouslySetInnerHTML={UIStrings.CROWD_SETTINGS.FIELDS.realmActiveDescription} />
            </NxCheckbox>
          </NxFormGroup>
          <NxFormGroup
              label={UIStrings.CROWD_SETTINGS.FIELDS.urlLabel}
              sublabel={UIStrings.CROWD_SETTINGS.FIELDS.urlDescription}
              isRequired>
            <NxTextInput
                className="nx-text-input--long"
                {...FormUtils.fieldProps('url', current)}
                onChange={FormUtils.handleUpdate('url', send)} />
          </NxFormGroup>
          {data.url?.startsWith('https') && <>
            <NxFormGroup
                label={UIStrings.CROWD_SETTINGS.FIELDS.useTrustStoreLabel}
                sublabel={<span dangerouslySetInnerHTML={UIStrings.CROWD_SETTINGS.FIELDS.useTrustStoreDescription}/>}
                isRequired>
              <NxCheckbox
                  {...FormUtils.checkboxProps('useTrustStoreForUrl', current)}
                  onChange={FormUtils.handleUpdate('useTrustStoreForUrl', send)}>
                {UIStrings.CROWD_SETTINGS.FIELDS.useTrustStoreText}
              </NxCheckbox>
            </NxFormGroup>
          </>}
          <NxFormGroup label={UIStrings.CROWD_SETTINGS.FIELDS.applicationNameLabel} isRequired>
            <NxTextInput
                autoComplete="off"
                {...FormUtils.fieldProps('applicationName', current)}
                onChange={FormUtils.handleUpdate('applicationName', send)}/>
          </NxFormGroup>
          <NxFormGroup label={UIStrings.CROWD_SETTINGS.FIELDS.applicationPasswordLabel} isRequired>
            <NxTextInput
                type="password"
                autoComplete="new-password"
                {...FormUtils.fieldProps('applicationPassword', current)}
                onChange={FormUtils.handleUpdate('applicationPassword', send)}/>
          </NxFormGroup>
          <NxFormGroup
              label={UIStrings.CROWD_SETTINGS.FIELDS.timeoutLabel}
              sublabel={UIStrings.CROWD_SETTINGS.FIELDS.timeoutDescription}>
            <NxTextInput
                {...FormUtils.fieldProps('timeout', current)}
                onChange={FormUtils.handleUpdate('timeout', send)}/>
          </NxFormGroup>
        </NxForm>
      </Section>
    </ContentBody>
  </Page>;
}
